import { Fragment, useEffect, useState } from "react";
import { AlertCircle, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { invApi } from "./api";

const pctCls = (p) => (p == null ? "text-stone-400" : p < 50 ? "text-red-600" : p < 65 ? "text-amber-600" : "text-emerald-700");

export function FoodCostView({ token }) {
  const [rows, setRows] = useState(null);
  const [showUnmapped, setShowUnmapped] = useState(false);
  const [open, setOpen] = useState("");

  useEffect(() => {
    invApi(token).get("/inventory/food-cost").then((r) => setRows(r.data)).catch(() => toast.error("Couldn't load food cost"));
  }, [token]);

  if (!rows) return <div className="text-center text-stone-400 py-10 text-sm">Loading…</div>;
  const mapped = rows.filter((r) => r.mapped);
  const shown = showUnmapped ? rows : mapped;
  const avg = mapped.filter((r) => r.margin_pct != null && !r.missing_cost.length);
  const avgPct = avg.length ? (avg.reduce((s, r) => s + r.margin_pct, 0) / avg.length).toFixed(1) : null;

  return (
    <div className="space-y-4" data-testid="food-cost-view">
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {[["Items costed", mapped.length, "stat-costed"], ["Avg margin", avgPct != null ? `${avgPct}%` : "—", "stat-avg-margin"], ["Missing costs", mapped.filter((r) => r.missing_cost.length).length, "stat-missing-cost"]].map(([t, v, tid]) => (
          <div key={t} className="bg-white border border-stone-200 rounded-2xl px-4 py-3">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{t}</div>
            <div className="text-2xl font-black tabular-nums text-stone-900" data-testid={tid}>{v}</div>
          </div>
        ))}
      </div>
      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-stone-200 flex items-center gap-2 flex-wrap">
          <DollarSign className="w-4 h-4 text-brand-red" />
          <div className="font-black text-stone-800 text-sm uppercase tracking-widest">Food cost report</div>
          <button type="button" onClick={() => setShowUnmapped((v) => !v)} data-testid="food-cost-toggle-unmapped"
            className={`ml-auto text-[10px] uppercase tracking-widest font-black px-3 py-1.5 rounded-full border ${showUnmapped ? "bg-stone-800 text-white border-stone-800" : "border-stone-200 text-stone-600"}`}>
            {showUnmapped ? "Hide unmapped" : "Show unmapped"}
          </button>
        </div>
        {shown.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-stone-400" data-testid="food-cost-empty">Map recipes and set a cost per unit on ingredients to see margins here.</div>
        ) : (
          <table className="w-full text-sm" data-testid="food-cost-table">
            <thead className="text-[10px] uppercase tracking-widest text-stone-500 font-bold bg-stone-50">
              <tr><th className="text-left px-4 py-2">Menu item</th><th className="text-right px-2 py-2">Price</th><th className="text-right px-2 py-2">Cost</th><th className="text-right px-4 py-2">Margin</th></tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {shown.map((r) => (
                <Fragment key={r.menu_item_id}>
                  <tr onClick={() => setOpen(open === r.menu_item_id ? "" : r.menu_item_id)} className={`cursor-pointer ${!r.mapped ? "opacity-50" : ""}`} data-testid={`food-cost-row-${r.menu_item_id}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-bold text-stone-800 flex items-center gap-1.5">{r.name}{r.missing_cost.length > 0 && <AlertCircle className="w-3.5 h-3.5 text-amber-500" title={`No cost set: ${r.missing_cost.join(", ")}`} />}</div>
                      <div className="text-[11px] text-stone-400">{r.category}{!r.mapped ? " · no recipe" : ""}</div>
                    </td>
                    <td className="px-2 py-2.5 text-right font-black tabular-nums">${r.price.toFixed(2)}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums text-stone-700" data-testid={`food-cost-cost-${r.menu_item_id}`}>{r.mapped ? `$${r.cost.toFixed(2)}` : "—"}</td>
                    <td className={`px-4 py-2.5 text-right font-black tabular-nums ${pctCls(r.mapped ? r.margin_pct : null)}`} data-testid={`food-cost-margin-${r.menu_item_id}`}>
                      {r.mapped && r.margin_pct != null ? <>${r.margin.toFixed(2)} <span className="text-[10px]">({r.margin_pct}%)</span></> : "—"}
                    </td>
                  </tr>
                  {open === r.menu_item_id && r.parts.length > 0 && (
                    <tr className="bg-stone-50">
                      <td colSpan={4} className="px-6 py-2">
                        <ul className="text-xs text-stone-600 grid sm:grid-cols-2 gap-x-6" data-testid={`food-cost-parts-${r.menu_item_id}`}>
                          {r.parts.map((p) => <li key={p.name} className="flex justify-between py-0.5"><span>{p.name} × {p.quantity} {p.unit}</span><span className="tabular-nums font-bold">{r.missing_cost.includes(p.name) ? <span className="text-amber-600">no cost</span> : `$${p.cost.toFixed(2)}`}</span></li>)}
                        </ul>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
